import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetGenericEvent} from 'features/core/shared/widget/event/widget-generic-event';
import {
    MvsWidgetFormGenericEventConstants
} from 'features/core/shared/widget/component/runtime/implementations/mvs-widget-form/mvs-widget-form-generic-event.constants';
import {MvsFormGroup} from 'features/core/shared/logic/form/mvs-form-group';
import {MvsFormDto} from 'features/core/shared/dto/form/dto/mvs-form.dto';
import {MvsFormFieldDto} from 'features/core/shared/dto/form/dto/mvs-form-field.dto';
import {
    MvsFormValueListEntryDto
} from 'features/core/shared/dto/form/dto/mvs-form-value-list-entry.dto';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {MvsMessageService} from 'features/core/shared/service/message/mvs-message.service';
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from 'features/core/shared/logic/object-change-information';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {ObjectRequestList} from 'features/core/shared/dto/object/request/object-request-list';
import {CpAgentTransactionService} from '../../../../../service/api/cp-agent-transaction.service';
import {CpFeatureRight} from '../../../../../service/domain/cp-feature-right';
import {
    ProvisionManualBookingRequestDto,
    ProvisionManualBookingResultDto
} from '../../../../../model/dto/api/provision-manual-booking.dto';
import {ProvisionAgentBalanceDto} from '../../../../../model/dto/entity/provision-agent-balance.dto';
import {
    ProvisionAgentTransactionTypeDto
} from '../../../../../model/dto/entity/provision-agent-transaction-type.dto';
import {ProvisionAccountKind} from '../../../../../enum/provision-account-kind.enum';
import {ProvisionBookingKind} from '../../../../../enum/provision-booking-kind.enum';
import {ProvisionTransactionTypeUsecase} from '../../../../../enum/provision-transaction-type-usecase.enum';

/**
 * Action dialog "Manual booking" (German UI label "Manuelle Buchung") —
 * `POST /mvsa/cp/provisionAgentTransactions/manualBooking`
 * (03-architektur-backend.md §11.1, V-007.1/V-007.2; um right `cp_manualBooking`).
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * FORM MECHANISM USED (design gate finding B-9, capability-matrix.md §5 — MANDATORY
 * DOCUMENTATION): TRANSIENT FORM WIDGET built from an `MvsFormDto` constructed in the frontend.
 *
 *   const form = new MvsFormDto();  form.formFields = { … MvsFormFieldDto … };
 *   WidgetFactory.createWidget(alias, name, 'form', 'transient', 'transient', objectType,
 *                              WidgetDataParam.create('form', form));
 *   <mvs-widget [widgetData]="…" (onGenericEvent)="…">
 *
 * The fields are rendered by the framework (`mvs-widget-form` -> `mvs-form` -> the registered
 * input field components); the captured values come back via
 * `MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE` together with `MvsFormGroup.status`.
 * This dialog contains NO hand-written form HTML — the template only holds the
 * `p-dialog` frame, the balance preview, and the action button.
 *
 * WHY NOT `MvsUiObjectService.openObjectViaDialog(...)`: that path writes via the
 * generic CRUD of the object type. The manual booking, however, MUST go to the custom endpoint
 * `/manualBooking` — only there is the ACTIVE status created without an activation run (V-007.1),
 * the contract-side R-09 counterpart (V-007.2), the type/valuta validation, the lock window
 * check P-13, and the synchronous balance recalc. The write therefore belongs to the caller,
 * the form is only for capture.
 *
 * REPO EVIDENCE for exactly this pattern: `features/feature-core/wf/component/protected-components/
 * wf-process-start/wf-process-start.component.ts` (start parameters of a workflow process:
 * MvsFormDto -> transient form widget -> own start button -> own endpoint).
 * ────────────────────────────────────────────────────────────────────────────────────────────
 *
 * BALANCE PREVIEW: booking type, amount, and the current balance yield the projected state
 * of the affected account. The account and sign ALWAYS come from the booking type
 * (`accountKind` + `bookingKind`) — never from a user input (03 §11.1: "account/direction
 * always come from the type"). The preview is a display, not a commitment: the binding
 * check is done by the backend.
 */
@Component({
    selector: 'cp-provision-agent-manual-booking-dialog',
    templateUrl: './cp-provision-agent-manual-booking-dialog.component.html',
    styleUrls: ['./cp-provision-agent-manual-booking-dialog.component.scss'],
    standalone: false
})
export class CpProvisionAgentManualBookingDialogComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    @Input() provisionAgentId: number;

    /** Visibility of the dialog; intended as a two-way binding (`[(visible)]`). */
    @Input() visible: boolean = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    /** Fires after a successfully written booking. */
    @Output() onBooked = new EventEmitter<ProvisionManualBookingResultDto>();

    /**
     * Fires when the server refused the um right `cp_manualBooking` with a 403.
     *
     * The demotion half of the module-wide OPTIMISTIC rights pattern (orchestrator decision OD-1,
     * reasoning in {@link CpFeatureRight}): the right cannot be known before acting — this
     * frontend has no feature-rights service and no cp payload carries a grant — so the action
     * starts open and a genuine refusal by `cp/cross/um/ProvisionFeatureGuard` demotes it. The
     * hosting pane listens and locks its entry button, so the user is not sent back into a dialog
     * the server will refuse again. Deliberate deviation from 04-frontend.md §5.1.
     */
    @Output() onForbidden = new EventEmitter<void>();

    /** The transient capture widget (see class Javadoc). */
    formWidget: WidgetData;

    /** Last valid form value; null as long as the form is incomplete/invalid. */
    formValue: any = null;

    /** Current balance of the agent — basis of the preview. */
    balanceDto: ProvisionAgentBalanceDto;

    /** Manually bookable booking types (usecase MANUAL or MIXED). */
    transactionTypes: ProvisionAgentTransactionTypeDto[] = [];

    /** Backend error message when `success = false`. */
    failureMessage: string = null;

    protected readonly ProvisionAccountKind = ProvisionAccountKind;

    constructor(
        protected coreService: MvsCoreService,
        protected mvsObjectService: MvsObjectService,
        protected messageService: MvsMessageService,
        protected agentTransactionService: CpAgentTransactionService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.initialized = true;
        if (this.visible) {
            this.refreshComponent();
        }
    }

    /**
     * Rebuilds the form and the preview data basis. Called on every open of the dialog,
     * so neither a stale form value nor an outdated balance is left standing.
     */
    refreshComponent(): void {

        this.formValue = null;
        this.failureMessage = null;
        this.formWidget = null;

        if (!this.provisionAgentId) {
            return;
        }

        this.busy = true;
        this.loadBalance();
        this.loadTransactionTypes();
    }

    /** Loads the agent's current balance (1:1 row via the registry alias). */
    private loadBalance(): void {

        const objectRequestList = ObjectRequestList.createBasic(
            false,
            [FilterCriteria.create('provisionAgent', FilterCriteria.cOperatorEqual, this.provisionAgentId)],
            null);

        this.coreService.getCrudService('cp.ProvisionAgentBalance')
            .list(objectRequestList)
            .subscribe({
                next: (dtoList) => {
                    const entries = dtoList?.entries as ProvisionAgentBalanceDto[];
                    this.balanceDto = entries && entries.length ? entries[0] : null;
                },
                error: () => {
                    this.balanceDto = null;
                }
            });
    }

    /**
     * Loads the manually bookable booking types and builds the transient form from them.
     *
     * Why the list here and not as an FK field of the form: the account and sign of the
     * preview come from the SELECTED booking type, so the component needs the type objects
     * anyway. The form field itself stays a normal select field with a value list — rendered
     * by the framework, not by hand.
     */
    private loadTransactionTypes(): void {

        const filterCriteria = [
            FilterCriteria.create('usecase', FilterCriteria.cOperatorNotEqual,
                ProvisionTransactionTypeUsecase.SYSTEM)
        ];

        const objectRequestList = ObjectRequestList.createBasic(false, filterCriteria, null);

        this.coreService.getCrudService('cp.ProvisionAgentTransactionType')
            .list(objectRequestList)
            .subscribe({
                next: (dtoList) => {
                    this.transactionTypes = (dtoList?.entries as ProvisionAgentTransactionTypeDto[]) ?? [];
                    this.buildFormWidget();
                    this.busy = false;
                },
                error: () => {
                    this.transactionTypes = [];
                    this.buildFormWidget();
                    this.busy = false;
                }
            });
    }

    /**
     * Builds the `MvsFormDto` of the manual booking and the transient form widget from it.
     * The field ids match the attributes of {@link ProvisionManualBookingRequestDto} 1:1,
     * so {@link handleBook} needs no renaming logic.
     */
    private buildFormWidget(): void {

        const form = new MvsFormDto();
        const formFields: MvsFormFieldDto[] = [];

        // Booking type — mandatory. Value list from the manually bookable types (usecase MANUAL/MIXED).
        const transactionTypeField = MvsFormFieldDto.createBasic(
            'transactionTypeId', 'select', 'Buchungsart');
        transactionTypeField.uiMandatory = true;
        transactionTypeField.order = 10;
        transactionTypeField.createValueList(
            ...this.transactionTypes.map(type =>
                MvsFormValueListEntryDto.createBasic(type.id, type.name ?? type.alias)));
        formFields['transactionTypeId'] = transactionTypeField;

        // Amount — mandatory, always positive; the sign comes from the booking type.
        const amountField = MvsFormFieldDto.createBasic('amount', 'amount', 'Betrag');
        amountField.uiMandatory = true;
        amountField.uiDoubleMinValue = 0.01;
        amountField.order = 20;
        formFields['amount'] = amountField;

        // Valuta date — mandatory; the backend checks admissibility against the type.
        const valutaDateField = MvsFormFieldDto.createBasic('valutaDate', 'date', 'Valutadatum');
        valutaDateField.uiMandatory = true;
        valutaDateField.order = 30;
        formFields['valutaDate'] = valutaDateField;

        // Reason — MANDATORY FIELD (03 §11.1 "mandatory reason").
        const referenceTextField = MvsFormFieldDto.createBasic('referenceText', 'textarea', 'Grund');
        referenceTextField.uiMandatory = true;
        referenceTextField.uiDescription = 'Pflichtangabe: warum wird manuell gebucht?';
        referenceTextField.order = 40;
        formFields['referenceText'] = referenceTextField;

        // Optional contract reference — additionally creates the contract-side counterpart (V-007.2).
        const provisionContractField = new MvsFormFieldDto();
        provisionContractField.id = 'provisionContractDtoId';
        provisionContractField.dataType = 'cp.ProvisionContract';
        provisionContractField.relationship = 'ManyToOne';
        provisionContractField.access = 'update';
        provisionContractField.uiLabel = 'Vertragsprovision (optional)';
        provisionContractField.uiDescription =
            'Gesetzt entsteht zusätzlich die vertragsseitige Gegenbuchung.';
        provisionContractField.order = 50;
        formFields['provisionContractDtoId'] = provisionContractField;

        form.formFields = formFields;

        /**
         * AI: Transient form widget for capturing a manual account booking.
         *     Source: MvsFormDto built in the frontend (no entity template) — the write
         *     goes to POST /cp/provisionAgentTransactions/manualBooking.
         *     Caseworkers use it to correct provision and reserve accounts in the review window.
         */
        this.formWidget = WidgetFactory.createWidget(
            'cp.agent.manualBooking.form',
            'Manuelle Buchung',
            'form',
            'transient',
            'transient',
            'cp.ProvisionAgentTransaction',
            WidgetDataParam.create('size', 'S'),
            WidgetDataParam.create('form', form)
        );
    }

    /**
     * Receives the form values. Only a FULLY valid form sets {@link formValue};
     * otherwise the action button stays locked.
     */
    handleGenericEvent(event: WidgetGenericEvent): void {

        if (event.eventName !== MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE) {
            return;
        }

        const formGroup: MvsFormGroup =
            event.object[MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE_ATTR_FORM_GROUP];

        this.formValue = formGroup.status === 'VALID' ? formGroup.value : null;
        this.failureMessage = null;
    }

    /** The currently selected booking type (for the account/sign of the preview). */
    get selectedTransactionType(): ProvisionAgentTransactionTypeDto {
        const id = this.formValue?.['transactionTypeId'];
        if (!id) {
            return null;
        }
        return this.transactionTypes.find(type => type.id == id) ?? null;
    }

    /** Affected account of the preview; null as long as no booking type is selected. */
    get previewAccountKind(): ProvisionAccountKind {
        const type = this.selectedTransactionType;
        return type ? type.accountKind : null;
    }

    /** Signed booking amount: CREDIT = credit note (+), DEBIT = receivable (−). */
    get previewSignedAmount(): number {
        const type = this.selectedTransactionType;
        const amount = Number(this.formValue?.['amount']);

        if (!type || !amount || isNaN(amount)) {
            return null;
        }
        return type.bookingKind === ProvisionBookingKind.DEBIT ? -amount : amount;
    }

    /** Balance of the affected account BEFORE the booking. */
    get previewBalanceBefore(): number {
        if (!this.balanceDto || this.previewAccountKind === null) {
            return null;
        }
        return this.previewAccountKind === ProvisionAccountKind.RESERVE_ACCOUNT
            ? this.balanceDto.reserveBalance
            : this.balanceDto.provisionBalance;
    }

    /** Balance of the affected account AFTER the booking (pure display, not a commitment). */
    get previewBalanceAfter(): number {
        const before = this.previewBalanceBefore;
        const signed = this.previewSignedAmount;

        if (before == null || signed == null) {
            return null;
        }
        return before + signed;
    }

    /**
     * True if the preview would push the reserve account into the negative — the hint
     * towards the payout brake (U-03), before the booking is even made.
     */
    get previewCausesUnderfunding(): boolean {
        return this.previewAccountKind === ProvisionAccountKind.RESERVE_ACCOUNT
            && this.previewBalanceAfter != null
            && this.previewBalanceAfter < 0;
    }

    /** The action button is only active with a fully valid form. */
    get submittable(): boolean {
        return !!this.formValue && !this.busy;
    }

    /**
     * Writes the booking to the custom endpoint.
     *
     * A business failure comes back as HTTP 200 with `success = false` (bm convention) —
     * so `success` is checked on EVERY response, and 2xx is never assumed to mean
     * "booked".
     */
    handleBook(): void {

        if (!this.submittable) {
            return;
        }

        const request: ProvisionManualBookingRequestDto = {
            provisionAgentId: this.provisionAgentId,
            transactionTypeId: Number(this.formValue['transactionTypeId']),
            amount: Number(this.formValue['amount']),
            valutaDate: this.toIsoDate(this.formValue['valutaDate']),
            referenceText: this.formValue['referenceText'],
            provisionContractId: this.formValue['provisionContractDtoId']
                ? Number(this.formValue['provisionContractDtoId'])
                : undefined
        };

        this.busy = true;
        this.failureMessage = null;

        this.agentTransactionService.manualBooking(request).subscribe({
            next: (result: ProvisionManualBookingResultDto) => {
                this.busy = false;

                if (!result || result.success !== true) {
                    this.failureMessage = result?.failureMessage
                        ?? 'Die Buchung konnte nicht ausgeführt werden.';
                    return;
                }

                this.messageService.showSuccessMessage(
                    'Manuelle Buchung', 'Die Buchung wurde angelegt.');

                this.informObjectChanged();
                this.onBooked.emit(result);
                this.handleClose();
            },
            error: (error) => {
                this.busy = false;

                // A 403 is the um right answering; anything else is a technical/validation
                // failure and must NOT demote the right (see onForbidden).
                if (CpFeatureRight.isForbidden(error)) {
                    this.failureMessage =
                        CpFeatureRight.deniedMessage(CpFeatureRight.MANUAL_BOOKING);
                    this.onForbidden.emit();
                    return;
                }

                this.failureMessage =
                    'Die Buchung konnte nicht ausgeführt werden. Bitte Eingaben prüfen.';
            }
        });
    }

    /**
     * Informs all listeners (balance tile, booking table) about the new booking.
     * Same channel the generic CRUD uses — so existing subscribers react without
     * extra wiring.
     */
    private informObjectChanged(): void {
        this.mvsObjectService.objectChangeEvent.next(
            ObjectChangeInformation.create(
                'cp.ProvisionAgentTransaction',
                ObjectChangeInformationActionEnum.created,
                null,
                null));
    }

    /** Date -> 'yyyy-MM-dd' (backend's LocalDate format). */
    private toIsoDate(value: any): string {
        if (!value) {
            return null;
        }
        const date = value instanceof Date ? value : new Date(value);
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${date.getFullYear()}-${month}-${day}`;
    }

    handleShow(): void {
        this.refreshComponent();
    }

    handleClose(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    handleVisibleChange(value: boolean): void {
        this.visible = value;
        this.visibleChange.emit(value);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['visible'] && this.visible) {
            this.refreshComponent();
        }
        if (changes['provisionAgentId'] && this.visible) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
    }

}
